import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  MessageSquare, 
  ImageIcon, 
  Video,
  Mic, 
  FileText, 
  Settings, 
  Plus,
  Trash2,
  PanelRightClose,
  PanelRight,
  Globe,
  Sparkles,
  History,
  Users
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import HelcooLogo from "./HelcooLogo";

export default function Sidebar({ onCollapsedChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  const handleCollapse = (value) => {
    setCollapsed(value);
    onCollapsedChange?.(value);
  };

  const navItems = [
    { path: "/chat", icon: MessageSquare, label: t("chat") },
    { path: "/assistants", icon: Users, label: t("assistants") },
    { path: "/image", icon: ImageIcon, label: t("images") },
    { path: "/video", icon: Video, label: t("video") },
    { path: "/speech", icon: Mic, label: t("voice") },
    { path: "/summarize", icon: FileText, label: t("summarize") },
    { path: "/history", icon: History, label: t("history") },
    { path: "/settings", icon: Settings, label: t("settings") },
  ];

  useEffect(() => {
    if (location.pathname.startsWith("/chat")) {
      fetchSessions();
    }
  }, [location.pathname]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/chat/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    navigate("/chat");
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/chat/session/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      if (location.pathname === `/chat/${sessionId}`) {
        navigate("/chat");
      }
    } catch (error) {
      console.error("Failed to delete session:", error);
    }
  };

  const isActive = (path) => {
    if (path === "/chat") return location.pathname.startsWith("/chat");
    return location.pathname === path;
  };

  return (
    <aside 
      className={cn(
        "sidebar flex flex-col h-screen",
        collapsed ? "w-[72px]" : "w-[280px]"
      )}
      data-testid="sidebar"
    >
      {/* Header with Logo */}
      <div className="sidebar-header justify-between">
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <Link to="/chat" className="flex items-center gap-3">
              <HelcooLogo size={40} animated={false} showBadge={true} />
              <span className="text-lg font-bold text-foreground">HELCOO</span>
            </Link>
          </div>
        ) : (
          <div className="mx-auto">
            <Link to="/chat">
              <HelcooLogo size={32} animated={false} showBadge={false} />
            </Link>
          </div>
        )}
        
        {!collapsed && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleCollapse(true)}
            className="h-8 w-8 rounded-lg hover:bg-secondary"
            data-testid="sidebar-toggle"
          >
            <PanelRightClose className="w-4 h-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      {/* Language Switcher */}
      <div className="px-3 py-2">
        <button
          onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
          className={cn(
            "lang-switcher w-full",
            collapsed && "justify-center px-3"
          )}
          data-testid="language-switcher"
        >
          <Globe className="w-4 h-4 flex-shrink-0" />
          {!collapsed && (
            <span className="font-medium text-sm">
              {language === "ar" ? "English" : "العربية"}
            </span>
          )}
        </button>
      </div>

      {/* Collapse Button (when collapsed) */}
      {collapsed && (
        <div className="flex justify-center py-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleCollapse(false)}
            className="h-8 w-8 rounded-lg hover:bg-secondary"
            data-testid="sidebar-expand"
          >
            <PanelRight className="w-4 h-4 text-muted-foreground" />
          </Button>
        </div>
      )}

      {/* Navigation */}
      <nav className="px-2 py-2">
        <div className="space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <div
                className={cn(
                  "sidebar-nav-item",
                  isActive(item.path) && "active",
                  collapsed && "justify-center px-3"
                )}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && (
                  <span className="font-medium truncate">{item.label}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </nav>

      {/* Chat Sessions - Only visible on Desktop */}
      {location.pathname.startsWith("/chat") && !collapsed && (
        <div className="hidden md:flex flex-1 flex-col min-h-0 border-t border-border">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t("chats")}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNewChat}
              className="h-6 w-6 rounded-md hover:bg-secondary"
              data-testid="new-chat-btn"
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
          
          {/* Scrollable sessions list */}
          <div className="flex-1 overflow-y-auto px-2 pb-4">
            <div className="space-y-0.5">
              {loading ? (
                <div className="px-4 py-2 text-xs text-muted-foreground">{t("loading")}</div>
              ) : sessions.length === 0 ? (
                <div className="px-4 py-3 text-center">
                  <Sparkles className="w-5 h-5 mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-xs text-muted-foreground">{t("noChats")}</p>
                </div>
              ) : (
                sessions.slice(0, 20).map((session) => (
                  <Link
                    key={session.id}
                    to={`/chat/${session.id}`}
                    data-testid={`session-${session.id}`}
                  >
                    <div
                      className={cn(
                        "session-item group",
                        location.pathname === `/chat/${session.id}` && "active"
                      )}
                    >
                      <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                      <span className="truncate flex-1">{session.title}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDeleteSession(e, session.id)}
                        className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity rounded hover:bg-destructive/10"
                        data-testid={`delete-session-${session.id}`}
                      >
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
